/**
 * AppointmentsDashboard — FASE 2: KPIs, gráfico, sesiones de hoy
 * Referencia: 0257-Maqueta-Desconfundir-Usuario-Agenda
 */
import React, { useMemo, useEffect } from "react";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";
import { Bar } from "react-chartjs-2";
import moment from "moment";
import "moment/locale/es";
import "moment/locale/pt";
import "moment/locale/en-gb";
import { useStyles } from "./styles";
import { i18n } from "../../translate/i18n";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default function AppointmentsDashboard({ appointments, onSelectAppointment }) {
  const classes = useStyles();

  useEffect(() => {
    const lang = (i18n.language || "es").split("-")[0];
    if (lang === "en") moment.locale("en-gb");
    else if (lang === "pt") moment.locale("pt");
    else moment.locale("es");
  }, [i18n.language]);

  const weekdayLabels = useMemo(
    () => [0, 1, 2, 3, 4, 5, 6].map((i) => i18n.t(`appointmentsModule.dashboard.wd${i}`)),
    [i18n.language],
  );

  const kpis = useMemo(() => {
    const today = moment().startOf("day");
    const weekStart = moment().startOf("week");
    const weekEnd = moment().endOf("week");
    const confirmed = appointments.filter((a) => a.status === "confirmed" || a.status === "completed");
    const pending = appointments.filter((a) => a.status === "pending");
    const todaySessions = appointments.filter((a) => moment(a.appointmentDate).isSame(today, "day"));
    const weekSessions = appointments.filter((a) =>
      moment(a.appointmentDate).isBetween(weekStart, weekEnd, null, "[]")
    );

    return {
      semana: weekSessions.length,
      pendientes: pending.length,
      hoy: todaySessions.length,
      tasa: appointments.length > 0
        ? Math.round((confirmed.length / appointments.length) * 100)
        : 0
    };
  }, [appointments]);

  const chartData = useMemo(() => {
    const weekStart = moment().startOf("week");
    const conf = [0, 0, 0, 0, 0, 0, 0];
    const pend = [0, 0, 0, 0, 0, 0, 0];
    const canc = [0, 0, 0, 0, 0, 0, 0];
    appointments.forEach((a) => {
      const d = moment(a.appointmentDate);
      if (d.isSame(weekStart, "week")) {
        const idx = d.day() === 0 ? 6 : d.day() - 1;
        if (a.status === "confirmed" || a.status === "completed") conf[idx]++;
        else if (a.status === "pending") pend[idx]++;
        else if (a.status === "cancelled") canc[idx]++;
      }
    });

    return {
      labels: weekdayLabels,
      datasets: [
        { label: i18n.t("appointmentsModule.dashboard.chartConfirmed"), data: conf, backgroundColor: "rgba(46, 125, 50, 0.7)" },
        { label: i18n.t("appointmentsModule.dashboard.chartPending"), data: pend, backgroundColor: "rgba(237, 108, 2, 0.7)" },
        { label: i18n.t("appointmentsModule.dashboard.chartCancelled"), data: canc, backgroundColor: "rgba(211, 47, 47, 0.7)" }
      ]
    };
  }, [appointments, weekdayLabels, i18n.language]);

  const todaySessions = useMemo(() => {
    const today = moment().startOf("day");
    return appointments
      .filter((a) => moment(a.appointmentDate).isSame(today, "day"))
      .sort((a, b) => new Date(a.appointmentDate) - new Date(b.appointmentDate));
  }, [appointments]);

  const proximasSessions = useMemo(() => {
    const tomorrow = moment().add(1, "day").startOf("day");
    const in30Days = moment().add(30, "days").endOf("day");
    return appointments
      .filter((a) => {
        const d = moment(a.appointmentDate);
        return d.isAfter(tomorrow) || d.isSame(tomorrow, "day");
      })
      .filter((a) => moment(a.appointmentDate).isBefore(in30Days) || moment(a.appointmentDate).isSame(in30Days, "day"))
      .filter((a) => a.status !== "cancelled" && a.status !== "canceled")
      .sort((a, b) => new Date(a.appointmentDate) - new Date(b.appointmentDate))
      .slice(0, 10);
  }, [appointments]);

  const formatTime = (dt) => (dt ? moment(dt).format("HH:mm") : "-");
  const formatDate = (dt) => (dt ? moment(dt).format("ddd D MMM") : "-");
  const detailLine = (a) => [
    a.confirmationCode ? `${i18n.t("appointmentsModule.dashboard.code")}: ${a.confirmationCode}` : null,
    a.durationMin ? `${a.durationMin} min` : null,
    a.modality ? i18n.t(`appointmentsModule.dashboard.modality.${a.modality}`, { defaultValue: a.modality }) : null,
    a.locationDetail || null,
    a.notes || null
  ].filter(Boolean).join(" · ");

  const getStatusLabel = (status) => {
    const k = ["pending", "confirmed", "completed", "rescheduled", "in_progress", "cancelled", "no_show"].includes(status)
      ? status
      : null;
    return k ? i18n.t(`appointmentsModule.status.${k}`) : status;
  };

  const getStatusColor = (status) => {
    if (status === "confirmed" || status === "completed" || status === "rescheduled") return "#2e7d32";
    if (status === "pending") return "#ed6c02";
    if (status === "in_progress") return "#1976d2";
    return "#757575";
  };

  const chartMonthTitle = useMemo(
    () => i18n.t("appointmentsModule.dashboard.chartTitle", { month: moment().format("MMMM YYYY") }),
    [i18n.language],
  );

  return (
    <div style={{ padding: 24 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md>
          <Paper className={classes.dashboardKpi} elevation={0}>
            <Typography variant="h4">{kpis.semana}</Typography>
            <Typography variant="body2" color="textSecondary">
              {i18n.t("appointmentsModule.dashboard.kpiWeek")}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md>
          <Paper className={classes.dashboardKpi} elevation={0}>
            <Typography variant="h4">{kpis.pendientes}</Typography>
            <Typography variant="body2" color="textSecondary">
              {i18n.t("appointmentsModule.dashboard.kpiPending")}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md>
          <Paper className={classes.dashboardKpi} elevation={0}>
            <Typography variant="h4">{kpis.hoy}</Typography>
            <Typography variant="body2" color="textSecondary">
              {i18n.t("appointmentsModule.dashboard.kpiToday")}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md>
          <Paper className={classes.dashboardKpi} elevation={0}>
            <Typography variant="h4">{kpis.tasa}%</Typography>
            <Typography variant="body2" color="textSecondary">
              {i18n.t("appointmentsModule.dashboard.kpiRate")}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
      <Paper className={classes.dashboardChart} elevation={0}>
        <Typography variant="h6" gutterBottom>
          {chartMonthTitle}
        </Typography>
        <div style={{ height: 200 }}>
          <Bar
            data={chartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: { legend: { position: "top" } },
              scales: { y: { beginAtZero: true } }
            }}
          />
        </div>
      </Paper>
      <Paper className={classes.dashboardToday} elevation={0}>
        <Typography variant="h6" gutterBottom>
          {i18n.t("appointmentsModule.dashboard.todayTitle")}
        </Typography>
        {todaySessions.length === 0 ? (
          <Typography variant="body2" color="textSecondary">{i18n.t("appointmentsModule.dashboard.todayEmpty")}</Typography>
        ) : (
          todaySessions.map((a) => (
            <div key={a.id} className={classes.dashboardTodayItem}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <Typography variant="body1">
                    {i18n.t("appointmentsModule.dashboard.todayLine", {
                      time: formatTime(a.appointmentDate),
                      contact: a.contactName || i18n.t("appointmentsModule.calendar.noName"),
                      service: a.serviceName || i18n.t("appointmentsModule.calendar.serviceFallback"),
                    })}
                  </Typography>
                  {a.professionalName && (
                    <Typography variant="caption" color="textSecondary">
                      {i18n.t("appointmentsModule.dashboard.professionalPrefix")} {a.professionalName}
                    </Typography>
                  )}
                  {detailLine(a) && (
                    <Typography variant="caption" color="textSecondary" display="block">
                      {detailLine(a)}
                    </Typography>
                  )}
                </div>
                <Button size="small" color="primary" variant="outlined" onClick={() => onSelectAppointment?.(a)}>
                  {i18n.t("appointmentsModule.dashboard.viewDetails")}
                </Button>
              </div>
            </div>
          ))
        )}
      </Paper>
      <Paper className={classes.dashboardToday} elevation={0} style={{ marginTop: 16 }}>
        <Typography variant="h6" gutterBottom>
          {i18n.t("appointmentsModule.dashboard.upcomingTitle")}
        </Typography>
        {proximasSessions.length === 0 ? (
          <Typography variant="body2" color="textSecondary">{i18n.t("appointmentsModule.dashboard.upcomingEmpty")}</Typography>
        ) : (
          proximasSessions.map((a) => (
            <div key={a.id} className={classes.dashboardTodayItem} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
              <div>
                <Typography variant="body1">
                  {formatDate(a.appointmentDate)} {formatTime(a.appointmentDate)} — {a.contactName || i18n.t("appointmentsModule.calendar.noName")} — {a.serviceName || i18n.t("appointmentsModule.calendar.serviceFallback")}
                </Typography>
                {a.professionalName && (
                  <Typography variant="caption" color="textSecondary">
                    {i18n.t("appointmentsModule.dashboard.professionalPrefix")} {a.professionalName}
                  </Typography>
                )}
                {detailLine(a) && (
                  <Typography variant="caption" color="textSecondary" display="block">
                    {detailLine(a)}
                  </Typography>
                )}
              </div>
              <span
                style={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  padding: "2px 8px",
                  borderRadius: 4,
                  backgroundColor: getStatusColor(a.status) + "20",
                  color: getStatusColor(a.status)
                }}
              >
                {getStatusLabel(a.status)}
              </span>
              <Button size="small" color="primary" variant="outlined" onClick={() => onSelectAppointment?.(a)}>
                {i18n.t("appointmentsModule.dashboard.viewDetails")}
              </Button>
            </div>
          ))
        )}
      </Paper>
    </div>
  );
}
